/**
 * Tooltip Component for OC Widget
 * Flexible tooltip that can wrap any element and show custom content
 * Based on online-community's OCFE Tooltip implementation
 */

import {
  arrow,
  flip,
  FloatingPortal,
  inline,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import colors from '@hl/ui-components/styles/colors.scss';
import React, { ReactElement, ReactNode, useRef } from 'react';
import styled from 'styled-components';

import { useSingleToggle } from '../../../../utils/useSingleToggle';
import { IOcAppTheme, OcColors } from '../../config/ocTheme';
import { withAlpha } from '../../styles/colorUtils';

const globalTTRef = {
  current: () => {},
};

/**
 * Trigger button that wraps children
 * Styled to be invisible and inherit parent styles while maintaining accessibility
 */
const TooltipButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  font: inherit;
  line-height: inherit;
  vertical-align: middle;

  /* Override global button style to restore browser's native focus-visible behavior */
  &:focus-visible {
    outline: -webkit-focus-ring-color auto 1px;
  }
`;

/**
 * Tooltip popover container
 */
const TooltipPopover = styled.div<{ $showing: boolean }>`
  background-color: ${({ theme }) => theme.color.chatBg || colors.white};
  color: ${({ theme }) => (theme as IOcAppTheme).color.textMuted || OcColors.metaText};
  border-radius: 6px;
  padding: 12px;
  max-width: 250px;
  border: 1px solid ${({ theme }) => withAlpha(theme.color.chatBorder, 0.4)};
  box-shadow: ${({ theme }) => `0 5px 10px ${withAlpha(theme.color.chatBorder, 0.2)}`};
  z-index: 9999;
  opacity: ${({ $showing }) => ($showing ? 1 : 0)};
  font-size: ${({ theme }) => theme.font.size.small};
  transition: opacity 0.2s ease;
`;

/**
 * Tooltip content with text alignment
 */
const TooltipContent = styled.div<{ $textAlign: 'center' | 'start' | 'end' }>`
  text-align: ${({ $textAlign }) => $textAlign};
  line-height: 1.4;
`;

export type OcTooltipProps = {
  /** Content to display in the tooltip */
  overlay: ReactElement | string;
  /** Element that triggers the tooltip (what user hovers over) */
  children: ReactNode;
  /** Tooltip placement relative to trigger */
  placement?: Placement;
  /** Text alignment within tooltip */
  textAlign?: 'center' | 'start' | 'end';
  /** Accessible title for screen readers */
  title?: string;
  /** Additional CSS class name */
  className?: string;
};

/**
 * OcTooltip Component
 * 
 * A flexible tooltip component that wraps any element and displays custom content.
 * Uses Floating UI for smart positioning and supports keyboard accessibility.
 * Matches the behavior of online-community's OCFE Tooltip.
 * 
 * @example
 * ```tsx
 * // Simple text tooltip
 * <OcTooltip overlay="Helpful information">
 *   <Icon icon={faInfoCircle} />
 * </OcTooltip>
 * 
 * // Rich content tooltip
 * <OcTooltip 
 *   overlay={
 *     <div>
 *       <strong>Title</strong>
 *       <p>Description text</p>
 *     </div>
 *   }
 *   placement="bottom"
 * >
 *   <button>Hover me</button>
 * </OcTooltip>
 * ```
 */
export const OcTooltip: React.FC<OcTooltipProps> = ({
  overlay,
  children,
  placement = 'top',
  textAlign = 'start',
  title = 'Additional information',
  className = '',
}) => {
  const [showing, toggle, show, hide] = useSingleToggle(globalTTRef);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: showing,
    onOpenChange: toggle,
    placement: placement,
    middleware: [
      inline(),
      offset(4),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const hover = useHover(context, {
    restMs: 300,
    delay: {
      open: 1000,
      close: 2000,
    },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
    hover,
  ]);

  return (
    <span className={className}>
      <TooltipButton
        ref={refs.setReference}
        {...getReferenceProps()}
        aria-label={title}
        aria-haspopup="dialog"
        onFocus={show}
        onBlur={hide}
        type="button"
      >
        {children}
      </TooltipButton>
      {showing && (
        <FloatingPortal>
          <TooltipPopover
            $showing={showing}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <TooltipContent $textAlign={textAlign}>{overlay}</TooltipContent>
          </TooltipPopover>
        </FloatingPortal>
      )}
    </span>
  );
};

export default OcTooltip;
